import React, { useEffect, useState } from 'react';
import { paymentAPI, studentAPI, hostelAPI, bankDetailAPI } from '../../services/api';
import { PageHeader, Button, Badge, Loading, Empty, Modal, Table, Card, StatCard, FormInput, FormSelect } from '../../components/common';
import { useAuth } from '../../context/AuthContext';
import { getPeriodLabel, getDueDateForPeriod } from '../../utils/paymentUtils';
import toast from 'react-hot-toast';
import { CreditCard, Plus, CheckCircle, Edit2 } from 'lucide-react';

const PERIODS = [{ value: 'H1', label: 'H1 (Jan-Jun)' }, { value: 'H2', label: 'H2 (Jul-Dec)' }];

export default function PaymentsPage() {
  const { user } = useAuth();
  const isStudent = user?.role === 'student';
  const [payments, setPayments] = useState([]);
  const [students, setStudents] = useState([]);
  const [hostels, setHostels] = useState([]);
  const [bankDetails, setBankDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [createModal, setCreateModal] = useState(false);
  const [payModal, setPayModal] = useState(null);
  const [bankModal, setBankModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({ student: '', amount: '', period: 'H1', year: new Date().getFullYear(), dueDate: '' });
  const [payForm, setPayForm] = useState({ paymentMethod: 'online', transactionId: '' });
  const [bankForm, setBankForm] = useState({ hostel: '', accountHolderName: '', accountNumber: '', ifscCode: '', bankName: '', branchName: '', upiId: '', phoneNumber: '' });

  const load = async () => {
    const params = {};
    if (filterStatus) params.status = filterStatus;
    const { data } = await paymentAPI.getAll(params);
    setPayments(data.data);
  };

  useEffect(() => {
    const init = async () => {
      await load();
      if (!isStudent) {
        const [studentsData, hostelsData] = await Promise.all([
          studentAPI.getAll(),
          hostelAPI.getAll(),
        ]);
        setStudents(studentsData.data.data);
        setHostels(hostelsData.data.data);
        
        // Load bank details
        try {
          const bankData = await bankDetailAPI.getAll();
          if (bankData.data.data?.length > 0) {
            setBankDetails(bankData.data.data[0]);
          }
        } catch (e) {
          // Bank details not set yet
        }
      }
      setLoading(false);
    };
    init();
  }, [filterStatus]);

  const handleCreate = async () => {
    setSaving(true);
    try {
      // Set due date using utility function
      const dueDate = form.dueDate || getDueDateForPeriod(form.period, form.year).toISOString().split('T')[0];
      await paymentAPI.create({ ...form, amount: Number(form.amount), year: Number(form.year), dueDate });
      toast.success('Payment record created');
      setCreateModal(false);
      load();
    } catch (e) { toast.error(e.response?.data?.message || 'Error creating payment'); }
    finally { setSaving(false); }
  };

  const handleMarkPaid = async () => {
    setSaving(true);
    try {
      await paymentAPI.markAsPaid(payModal._id, payForm);
      toast.success('Payment marked as paid');
      setPayModal(null);
      load();
    } catch (e) { toast.error('Error updating payment'); }
    finally { setSaving(false); }
  };

  const handleSaveBankDetails = async () => {
    setSaving(true);
    try {
      if (bankDetails) {
        // Update existing bank details
        await bankDetailAPI.update(bankDetails._id, bankForm);
        toast.success('Bank details updated');
        setBankDetails({ ...bankDetails, ...bankForm });
      } else {
        // Create new bank details
        const { data } = await bankDetailAPI.create(bankForm);
        toast.success('Bank details saved');
        setBankDetails(data.data);
      }
      setBankModal(false);
    } catch (e) { 
      toast.error(e.response?.data?.message || 'Error saving bank details'); 
    }
    finally { setSaving(false); }
  };

  const totalPaid = payments.filter(p => p.status === 'paid').reduce((s, p) => s + p.amount, 0);
  const totalPending = payments.filter(p => p.status === 'pending').reduce((s, p) => s + p.amount, 0);

  if (loading) return <Loading />;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Payment Management"
        description="Track rent payments and dues"
        action={!isStudent && (
          <Button onClick={() => { setForm({ student: students[0]?._id || '', amount: 5500, period: 'H1', year: new Date().getFullYear(), dueDate: '' }); setCreateModal(true); }}>
            <Plus className="w-4 h-4" /> Add Payment
          </Button>
        )}
      />

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard title="Total Collected" value={`₹${(totalPaid / 1000).toFixed(1)}K`} icon={CreditCard} color="green" />
        <StatCard title="Pending Amount" value={`₹${(totalPending / 1000).toFixed(1)}K`} icon={CreditCard} color="orange" />
        <StatCard title="Total Records" value={payments.length} icon={CreditCard} color="blue" />
      </div>

      {/* Filter */}
      <Card className="p-4">
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="border border-slate-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-300">
          <option value="">All Status</option>
          <option value="paid">Paid</option>
          <option value="pending">Pending</option>
          <option value="overdue">Overdue</option>
        </select>
      </Card>

      {/* Bank Details Section */}
      {!isStudent && (
        <Card className="p-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h3 className="font-display font-semibold text-slate-900">Bank Account Details</h3>
              <p className="text-sm text-slate-500 mt-1">Manage bank details where students can send payments</p>
            </div>
            <Button onClick={() => {
              if (bankDetails) {
                setBankForm(bankDetails);
              } else if (hostels.length > 0) {
                setBankForm({ ...bankForm, hostel: hostels[0]._id });
              }
              setBankModal(true);
            }} size="sm">
              <Edit2 className="w-4 h-4" /> {bankDetails ? 'Edit Details' : 'Add Bank Details'}
            </Button>
          </div>

          {bankDetails ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-slate-50 p-4 rounded-xl">
              <div>
                <p className="text-xs text-slate-500 uppercase font-semibold">Account Holder</p>
                <p className="text-sm font-medium text-slate-900 mt-1">{bankDetails.accountHolderName}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase font-semibold">Bank Name</p>
                <p className="text-sm font-medium text-slate-900 mt-1">{bankDetails.bankName}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase font-semibold">Account Number</p>
                <p className="text-sm font-mono font-medium text-slate-900 mt-1">{bankDetails.accountNumber}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase font-semibold">IFSC Code</p>
                <p className="text-sm font-mono font-medium text-slate-900 mt-1">{bankDetails.ifscCode}</p>
              </div>
              {bankDetails.upiId && (
                <div>
                  <p className="text-xs text-slate-500 uppercase font-semibold">UPI ID</p>
                  <p className="text-sm font-mono font-medium text-slate-900 mt-1">{bankDetails.upiId}</p>
                </div>
              )}
              {bankDetails.branchName && (
                <div>
                  <p className="text-xs text-slate-500 uppercase font-semibold">Branch Name</p>
                  <p className="text-sm font-medium text-slate-900 mt-1">{bankDetails.branchName}</p>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-6 bg-slate-50 rounded-xl">
              <CreditCard className="w-12 h-12 text-slate-300 mx-auto mb-2" />
              <p className="text-slate-500 text-sm">No bank details added yet</p>
            </div>
          )}
        </Card>
      )}

      {/* Table */}
      <Card>
        <Table headers={['Student', 'Period/Year', 'Amount', 'Due Date', 'Status', 'Payment Date', 'Actions']}>
          {payments.map(p => (
            <tr key={p._id} className="hover:bg-slate-50 transition">
              <td className="py-3 px-4">
                <p className="font-medium text-slate-900 text-sm">{p.student?.name}</p>
                <p className="text-slate-400 text-xs">{p.student?.rollNumber}</p>
              </td>
              <td className="py-3 px-4 text-sm text-slate-600">{getPeriodLabel(p.period)} {p.year}</td>
              <td className="py-3 px-4 text-sm font-semibold text-slate-900">₹{p.amount?.toLocaleString('en-IN')}</td>
              <td className="py-3 px-4 text-sm text-slate-600">{p.dueDate ? new Date(p.dueDate).toLocaleDateString('en-IN') : '-'}</td>
              <td className="py-3 px-4"><Badge status={p.status} /></td>
              <td className="py-3 px-4 text-sm text-slate-500">{p.paymentDate ? new Date(p.paymentDate).toLocaleDateString('en-IN') : '-'}</td>
              <td className="py-3 px-4">
                {p.status !== 'paid' && !isStudent && (
                  <Button size="sm" variant="success" onClick={() => { setPayModal(p); setPayForm({ paymentMethod: 'online', transactionId: '' }); }}>
                    <CheckCircle className="w-3.5 h-3.5" /> Mark Paid
                  </Button>
                )}
                {p.status === 'paid' && p.receiptNumber && (
                  <span className="text-xs text-slate-400 font-mono">{p.receiptNumber}</span>
                )}
              </td>
            </tr>
          ))}
        </Table>
        {payments.length === 0 && <Empty icon={CreditCard} title="No payment records" description="Payment records will appear here" />}
      </Card>

      {/* Create modal */}
      <Modal open={createModal} onClose={() => setCreateModal(false)} title="Add Payment Record">
        <div className="space-y-4">
          <FormSelect label="Student *" value={form.student} onChange={e => setForm({ ...form, student: e.target.value })}>
            {students.map(s => <option key={s._id} value={s._id}>{s.name} ({s.rollNumber})</option>)}
          </FormSelect>
          <div className="grid grid-cols-2 gap-3">
            <FormSelect label="Period *" value={form.period} onChange={e => setForm({ ...form, period: e.target.value })}>
              {PERIODS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
            </FormSelect>
            <FormInput label="Year *" type="number" value={form.year} onChange={e => setForm({ ...form, year: e.target.value })} />
          </div>
          <FormInput label="Amount (₹) *" type="number" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} />
          <FormInput label="Due Date" type="date" value={form.dueDate} onChange={e => setForm({ ...form, dueDate: e.target.value })} />
          <div className="flex gap-3 pt-2">
            <Button variant="secondary" className="flex-1" onClick={() => setCreateModal(false)}>Cancel</Button>
            <Button className="flex-1" loading={saving} onClick={handleCreate}>Create Record</Button>
          </div>
        </div>
      </Modal>

      {/* Mark paid modal */}
      <Modal open={!!payModal} onClose={() => setPayModal(null)} title="Mark Payment as Paid">
        <div className="space-y-4">
          <div className="bg-emerald-50 rounded-xl p-4">
            <p className="text-sm font-medium text-emerald-800">{payModal?.student?.name} – {payModal?.month} {payModal?.year}</p>
            <p className="text-lg font-bold text-emerald-900">₹{payModal?.amount?.toLocaleString('en-IN')}</p>
          </div>
          <FormSelect label="Payment Method" value={payForm.paymentMethod} onChange={e => setPayForm({ ...payForm, paymentMethod: e.target.value })}>
            <option value="online">Online Transfer</option>
            <option value="upi">UPI</option>
            <option value="cash">Cash</option>
            <option value="bank_transfer">Bank Transfer</option>
            <option value="cheque">Cheque</option>
          </FormSelect>
          <FormInput label="Transaction ID" value={payForm.transactionId} onChange={e => setPayForm({ ...payForm, transactionId: e.target.value })} placeholder="Optional" />
          <div className="flex gap-3 pt-2">
            <Button variant="secondary" className="flex-1" onClick={() => setPayModal(null)}>Cancel</Button>
            <Button variant="success" className="flex-1" loading={saving} onClick={handleMarkPaid}>Confirm Payment</Button>
          </div>
        </div>
      </Modal>

      {/* Bank Details Modal */}
      <Modal open={bankModal} onClose={() => setBankModal(false)} title={bankDetails ? 'Update Bank Details' : 'Add Bank Details'}>
        <div className="space-y-4">
          {hostels.length > 0 && !bankDetails && (
            <FormSelect label="Hostel *" value={bankForm.hostel} onChange={e => setBankForm({ ...bankForm, hostel: e.target.value })}>
              {hostels.map(h => <option key={h._id} value={h._id}>{h.name}</option>)}
            </FormSelect>
          )}
          <FormInput label="Account Holder Name *" value={bankForm.accountHolderName} onChange={e => setBankForm({ ...bankForm, accountHolderName: e.target.value })} placeholder="e.g., Hostel Management" />
          <FormInput label="Account Number *" value={bankForm.accountNumber} onChange={e => setBankForm({ ...bankForm, accountNumber: e.target.value })} placeholder="e.g., 1234567890" />
          <div className="grid grid-cols-2 gap-3">
            <FormInput label="IFSC Code *" value={bankForm.ifscCode} onChange={e => setBankForm({ ...bankForm, ifscCode: e.target.value.toUpperCase() })} placeholder="e.g., SBIN0001234" />
            <FormInput label="Bank Name *" value={bankForm.bankName} onChange={e => setBankForm({ ...bankForm, bankName: e.target.value })} placeholder="e.g., State Bank of India" />
          </div>
          <FormInput label="Branch Name" value={bankForm.branchName} onChange={e => setBankForm({ ...bankForm, branchName: e.target.value })} placeholder="Optional" />
          <FormInput label="UPI ID" value={bankForm.upiId} onChange={e => setBankForm({ ...bankForm, upiId: e.target.value })} placeholder="e.g., hostel@upi" />
          <FormInput label="Phone Number" value={bankForm.phoneNumber} onChange={e => setBankForm({ ...bankForm, phoneNumber: e.target.value })} placeholder="Optional" />
          <div className="flex gap-3 pt-2">
            <Button variant="secondary" className="flex-1" onClick={() => setBankModal(false)}>Cancel</Button>
            <Button className="flex-1" loading={saving} onClick={handleSaveBankDetails}>Save Details</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
